import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { PrimaryButton } from '@bb-ui/react-library/dist/components/Button';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';

import { useSharedStyles } from '../../utilities/sharedStyles';
import { RatingScale } from './RatingScale';
import { useStyles } from './FeedbackWidget.styles';
import { SubmitErrorKind, SubmitStatus } from './useSubmitFeedback';
import { IFeedbackDraft } from './FeedbackWidget.types';

export interface IFeedbackFormProps {
    /** The question heading the form, already naming the feature where it can. */
    title: string;
    status: SubmitStatus;
    errorKind?: SubmitErrorKind;
    draft: IFeedbackDraft;
    onDraftChange: (draft: IFeedbackDraft) => void;
    onSubmit: (rating: number, comment: string) => void;
    titleId: string;
}

const COMMENT_MAX_LENGTH = 1000;

/** How close to the limit the comment gets before a screen reader is told. */
const NEAR_LIMIT = 50;

export const FeedbackForm: React.FunctionComponent<IFeedbackFormProps> = ({
    title,
    status,
    errorKind,
    draft,
    onDraftChange,
    onSubmit,
    titleId,
}) => {
    const classes = useStyles();
    const sharedClasses = useSharedStyles();
    const { t } = useTranslation();

    const { rating, comment } = draft;

    // Announced only near and at the limit, not on every key.
    const remaining = COMMENT_MAX_LENGTH - comment.length;
    let limitNotice = '';
    if (remaining === 0) limitNotice = t('feedback.form.limitReached');
    else if (remaining <= NEAR_LIMIT)
        limitNotice = t('feedback.form.nearLimit');

    const submitting = status === 'submitting';

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (submitting || rating === undefined) return;
        onSubmit(rating, comment);
    };

    return (
        <form
            className={classes.form}
            onSubmit={handleSubmit}
            data-testid="feedback-form"
        >
            <Typography
                variant="h3"
                component="h2"
                id={titleId}
                className={classes.title}
            >
                {title}
            </Typography>

            <RatingScale
                value={rating}
                onChange={next => onDraftChange({ ...draft, rating: next })}
                disabled={submitting}
                labelledBy={titleId}
            />

            <TextField
                id="feedback-comment"
                label={t('feedback.form.commentLabel')}
                placeholder={t('feedback.form.commentPlaceholder')}
                multiline
                rows={4}
                fullWidth
                disabled={submitting}
                value={comment}
                // On the textarea itself, not the TextField wrapper.
                inputProps={{
                    maxLength: COMMENT_MAX_LENGTH,
                    'data-testid': 'feedback-comment',
                    // Inline, as a class may not override MUI's resize: none.
                    // Vertical only, so it stays inside the panel.
                    style: { resize: 'vertical', maxHeight: '50vh' },
                }}
                onChange={event =>
                    onDraftChange({ ...draft, comment: event.target.value })
                }
                helperText={t('feedback.form.characterCount', {
                    count: comment.length,
                    max: COMMENT_MAX_LENGTH,
                })}
                FormHelperTextProps={{
                    className: classes.commentCounter,
                    // @ts-expect-error data attributes pass through to the element
                    'data-testid': 'feedback-comment-counter',
                }}
                className={classes.comment}
            />
            {/* Always rendered, so the notice is announced when it appears: a
                live region added together with its text is often missed. */}
            <span
                className={sharedClasses.srOnly}
                aria-live="polite"
                data-testid="feedback-comment-limit"
            >
                {limitNotice}
            </span>

            {errorKind && (
                <Typography
                    component="p"
                    role="alert"
                    className={classes.error}
                    data-testid="feedback-error"
                >
                    {t(`feedback.form.errors.${errorKind}`)}
                </Typography>
            )}

            <PrimaryButton
                type="submit"
                disabled={rating === undefined}
                // aria-disabled while sending: disabled would drop its focus.
                aria-disabled={submitting || undefined}
                className={classes.submitButton}
                data-testid="feedback-submit"
            >
                {submitting
                    ? t('feedback.form.submitting')
                    : t('feedback.form.submit')}
            </PrimaryButton>
        </form>
    );
};
